const restaurantReservationTemplate = {
  channel: "voice",
  business_summary:
    "A restaurant taking reservation calls from diners.",
  industry_tag: "Restaurant",
  goal: {
    primary: "Book a table",
    summary:
      "Greet the caller, confirm party size + time, and book the reservation on the calendar.",
  },
  fields: [
    {
      key: "restaurant_name",
      label: "Restaurant name",
      type: "text",
      value: null,
      source: "missing",
      required: true,
      placeholder: "e.g. Bistro Lumière",
      can_skip: true,
      skip_fallback: "our restaurant",
    },
    {
      key: "hours",
      label: "Hours of operation",
      type: "textarea",
      value: "Tue–Sun 17:00 to 22:30, closed Mondays",
      source: "default",
    },
    {
      key: "max_party_size",
      label: "Largest party we'll book over the phone",
      type: "number",
      value: 8,
      source: "default",
    },
    {
      key: "private_dining_available",
      label: "Do we offer private dining?",
      type: "select",
      options: ["Yes", "No"],
      value: "No",
      source: "default",
    },
  ],
  recommended_tools: [
    {
      tool_key: "google_calendar",
      reason: "Hold the reservation slot on the floor calendar.",
    },
    {
      tool_key: "twilio_sms",
      reason: "Text the diner a confirmation after booking.",
    },
  ],
  recommended_contact_syncs: [
    {
      tool_key: "crm_picker",
      fields_to_be_synced: {
        first_name: "first name",
        phone: "phone number captured",
        party_size: "party size",
        reservation_time: "reserved date and time",
      },
      reason: "Track repeat diners and their preferences.",
    },
  ],
  recommended_conversation_syncs: [],
  recommended_outcomes: [
    {
      key: "book_reservation",
      label: "Book the reservation on my calendar",
      default_on: true,
      mechanism: "tool",
      tool_key: "google_calendar",
    },
    {
      key: "sms_confirmation",
      label: "Text a confirmation to the diner",
      default_on: true,
      mechanism: "tool",
      tool_key: "twilio_sms",
    },
    {
      key: "save_diner",
      label: "Save the diner as a contact",
      default_on: false,
      mechanism: "contact_sync",
      tool_key: "crm_picker",
    },
  ],
  voice_defaults: {
    first_sentence:
      "Thanks for calling {{restaurant_name}} — would you like to make a reservation?",
    speaks_first: "bot",
    detect_voicemail: "hangup",
    voicemail_message:
      "We're sorry we missed you. Please leave your name, number, and the date you'd like to reserve, and we'll call you right back.",
    max_call_duration: 480,
    use_turn_detector: true,
    listen_silence: 25,
    listen_silence_message: "Are you still there?",
  },
  behavior_defaults: {
    llm_temperature: 0.4,
    has_human_agent: false,
    use_tools: true,
    summarize: null,
  },
  prompt_skeleton: `You are the reservations host for {{restaurant_name}}.

Operating context:
- Hours: {{hours}}
- Largest party we'll book by phone: {{max_party_size}}
- Private dining: {{private_dining_available}}

Conversation flow:
1. Opening — greet warmly: "Thanks for calling {{restaurant_name}}! Would you like to make a reservation?"
2. Date + time — ask which date and time they're hoping for. If we're closed that day, politely flag it and suggest the nearest open days. If the time is outside our hours, suggest the nearest available time.
3. Party size — ask how many people. If > {{max_party_size}}, politely explain we route larger parties through the manager: "For a group that size we coordinate through our manager — let me take your name and number and have them call you back today."
4. Special occasion / dietary — ask if it's a special occasion (birthday / anniversary — we'll note it for the team) and if anyone has dietary needs (vegan, gluten-free, severe allergies). Capture this in the booking notes.
5. Name + phone — full name + mobile. MIRROR the phone number back digit-by-digit.
6. Find a slot — use the google_calendar tool to confirm a table is available at that date + time + party size. If yes, book it. If not:
   - Offer the nearest options (15-30 min earlier/later, or next day same time). Don't ask for date again — just propose alternatives.
   - If everything that day is booked, offer the next 2 open dates at the same time.
7. Confirm — read the booking back fully: date, day of week, time, party size, name, phone, any notes. Get an explicit "yes that works."
8. SMS confirmation — call the twilio_sms tool to text them the confirmation. If it errors, just continue verbally (don't tell them the SMS failed; just say "you'll get a confirmation by text shortly").
9. Close — quick note on what to expect: "We hold reservations for 15 minutes past the reservation time, so try to arrive a few minutes early. Anything else?"

Turn-taking rules:
- Voice: 1-2 sentence turns. One question at a time. Mirror back phone numbers and dates digit-by-digit.

Tone:
Warm, gracious, slightly upscale to match the restaurant's vibe. Never rush. Smile through your voice — even if the caller is brisk. Match their energy if they're chatty.

Boundaries — never:
- Promise a specific table (window, patio, by the bar) — that's the host's call on the night.
- Quote menu prices unless explicitly asked, and only ranges ("entrées run $28-42").
- Promise to seat them early or hold a table past 15 min.
- Confirm a reservation the calendar tool didn't actually accept.

Fallbacks:
If asked about the menu, dress code, parking, kid-friendliness, or anything we haven't covered: answer if you're confident, otherwise say "let me have the manager call you to confirm" and capture the question.

Escalation:
- Party size > {{max_party_size}}, dietary need that requires kitchen advance prep (severe shellfish allergy, full vegan tasting menu) → take their info and have the manager call back.
- Caller frustrated or asks for a manager → take their name + number and tell them the manager will call within the hour.`,
  confidence: 1.0,
  missing_critical_fields: ["restaurant_name"],
};

export default restaurantReservationTemplate;
